import { defineStore } from 'pinia';
import { computed, ref, shallowRef } from 'vue';
import {
  buildSearchIndex,
  DEFAULT_CARD_ID,
  searchCards,
  SUIT_LABEL,
  type CardId,
  type Locale,
  type SearchableCard,
  type SearchIndex,
  type Suit,
} from 'shared';
import cardsData from '@generated/cards.json';

/**
 * The card catalogue.
 *
 * Structure, names and keywords are bundled so the deck renders and the
 * filter works on a cold, offline start. The longer free meanings are fetched
 * once per locale from a precached static file. In-depth text is not here at
 * all — it is paid content and only ever comes from the authenticated API.
 */

export interface CatalogCard extends SearchableCard {
  tags: string[];
  art: Record<string, string>;
}

export interface FreeMeaning {
  full: string;
  reversed: string;
  source: string;
  fallbackLocale?: Locale;
}

const CARDS = cardsData as unknown as CatalogCard[];

export const useContentStore = defineStore('content', () => {
  const cards = shallowRef<CatalogCard[]>(CARDS);
  const index = shallowRef<SearchIndex>(buildSearchIndex(CARDS));
  const meanings = ref<Record<Locale, Record<CardId, FreeMeaning> | null>>({
    en: null,
    es: null,
  });
  const loading = ref(false);
  const error = ref<string | null>(null);

  const byId = computed(() => new Map(cards.value.map((card) => [card.id, card])));

  /**
   * Loads a locale's free meanings. Idempotent, and both locales can be held
   * at once so switching language offline does not need the network.
   */
  async function loadMeanings(locale: Locale): Promise<void> {
    if (meanings.value[locale]) return;

    loading.value = true;
    error.value = null;
    try {
      const response = await fetch(`/content/meanings.${locale}.json`);
      if (!response.ok) throw new Error(`Failed to load ${locale} meanings`);
      meanings.value = {
        ...meanings.value,
        [locale]: (await response.json()) as Record<CardId, FreeMeaning>,
      };
    } catch (cause) {
      error.value = cause instanceof Error ? cause.message : 'Failed to load card meanings';
    } finally {
      loading.value = false;
    }
  }

  function meaningFor(cardId: CardId, locale: Locale): FreeMeaning | null {
    return meanings.value[locale]?.[cardId] ?? meanings.value.en?.[cardId] ?? null;
  }

  function nameFor(cardId: CardId, locale: Locale): string {
    const card = byId.value.get(cardId);
    return card?.names[locale] ?? card?.names.en ?? cardId;
  }

  function keywordsFor(cardId: CardId, locale: Locale): string[] {
    const card = byId.value.get(cardId);
    return card?.keywords[locale] ?? card?.keywords.en ?? [];
  }

  function artFor(cardId: CardId, deckSlug: string): string {
    const card = byId.value.get(cardId);
    return card?.art[deckSlug] ?? card?.art.rws ?? '/cards/placeholder.webp';
  }

  function suitLabel(suit: Suit, locale: Locale): string {
    return SUIT_LABEL[suit][locale];
  }

  /** Card ids matching a raw, un-submitted filter string, in display order. */
  function filter(query: string): CardId[] {
    return searchCards(index.value, query);
  }

  const allCardIds = computed(() =>
    [...cards.value].sort((a, b) => a.sortOrder - b.sortOrder).map((c) => c.id),
  );

  return {
    cards,
    byId,
    allCardIds,
    loading,
    error,
    defaultCardId: DEFAULT_CARD_ID,
    loadMeanings,
    meaningFor,
    nameFor,
    keywordsFor,
    artFor,
    suitLabel,
    filter,
  };
});
